// frontend/packages/business-console/src/utils/contentApi.js
import { api } from '@upryzze/services';

const ensure = (v, name) => {
  const s = String(v || '').trim();
  if (!s) throw new Error(`${name} is required.`);
  return s;
};

// The content routes answer with `{ error: "..." }`, and axios does not lift
// that onto the Error it throws. Every failure therefore reached the editor as
// the bare string "Request failed with status code 404" - which tells the
// business owner nothing and tells us nothing either. Turn the common ones
// into something a non-technical operator can act on, and fall back to the
// server's own text rather than axios's.
function contentError(err, fallback) {
  const status = err?.response?.status;
  const serverText =
    err?.response?.data?.error || err?.response?.data?.message || '';

  if (err?.code === 'ECONNABORTED') {
    return new Error(
      'That took too long to respond. It may still have gone through, so refresh before trying again.'
    );
  }
  if (status === 404 && /BusinessSiteConfig/i.test(serverText)) {
    return new Error(
      'This site is not connected to a content repository yet. Contact UpRyzze support so we can finish the setup.'
    );
  }
  if (status === 400 && /repo not configured/i.test(serverText)) {
    return new Error(
      'This site is not connected to a content repository yet. Contact UpRyzze support so we can finish the setup.'
    );
  }
  if (status === 409) {
    return new Error(
      'This page changed since you opened it (someone else edited it, or it was published). Refresh the list and reopen the page, then reapply your change.'
    );
  }
  if (status === 403) {
    return new Error('You do not have permission to change this content.');
  }
  if (status === 429) {
    return new Error(
      'That was a lot of requests at once. Wait a moment, then try again.'
    );
  }
  if (serverText) return new Error(serverText);
  return new Error(fallback);
}

const normEnv = (env) => {
  const e = String(env || 'uat').toLowerCase();
  return e === 'prod' ? 'prod' : 'uat';
};

// NOTE: business context is supplied via x-business-id header (axios interceptor).
export async function listContentFiles(env = 'uat') {
  try {
    const { data } = await api.get(`/v2/content`, {
      params: { env: normEnv(env) },
    });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw contentError(err, 'Could not load your pages. Please try again.');
  }
}

// Bounded SEO assist: ask the backend to suggest a meta title + description
// from the current page body. Read-only; the operator applies the result by
// hand. Returns null when no usable suggestion could be produced.
export async function suggestSeo({ body, title } = {}) {
  const { data } = await api.post(`/v2/content/seo-suggestion`, {
    body: String(body || ''),
    title: String(title || ''),
  });
  return data?.ok ? data.suggestion : null;
}

// CMS copilot: ask the backend to revise the doc being edited from a plain
// instruction. Read-only; returns a per-field proposal (`{ summary, changes }`)
// the operator applies with one click. Returns null when nothing usable came
// back.
export async function reviseContent({ instruction, fields } = {}) {
  const { data } = await api.post(`/v2/content/revise`, {
    instruction: String(instruction || ''),
    fields: fields && typeof fields === 'object' ? fields : {},
  });
  return data?.ok ? data.revision : null;
}

export async function getContentSchema(collection, env = 'uat') {
  const col = ensure(collection, 'collection');
  try {
    const { data } = await api.get(`/v2/content/schema`, {
      params: { collection: col, env: normEnv(env) },
    });
    return data?.fields || null;
  } catch (err) {
    throw contentError(
      err,
      'Could not load the fields for that page type. Please try again.'
    );
  }
}

export async function getContentFile(path, env = 'uat') {
  const p = ensure(path, 'path');
  try {
    const { data } = await api.get(`/v2/content/file`, {
      params: { path: p, env: normEnv(env) },
    });
    return { sha: data?.sha || '', content: data?.content || '' };
  } catch (err) {
    throw contentError(err, 'Could not open that page. Please try again.');
  }
}

export async function saveContentFile(path, content, sha = '', env = 'uat') {
  const p = ensure(path, 'path');
  const body = { content: String(content ?? '') };
  if (sha) body.sha = sha;

  try {
    await api.put(`/v2/content/file`, body, {
      params: { path: p, env: normEnv(env) },
    });
  } catch (err) {
    throw contentError(err, 'Could not save your draft. Please try again.');
  }
}

export async function deleteContentFile(path, sha, env = 'uat') {
  const p = ensure(path, 'path');
  const s = ensure(sha, 'sha');
  try {
    await api.delete(`/v2/content/file`, {
      params: { path: p, sha: s, env: normEnv(env) },
    });
  } catch (err) {
    throw contentError(err, 'Could not delete that page. Please try again.');
  }
}

export async function getPublishStatus() {
  const { data } = await api.get(`/v2/content/status`);
  return data || null;
}

export async function publishDraftToProd() {
  try {
    // A publish is a GitHub merge, not a model call, so the 30s default is
    // fine; the point here is that its failures read like English.
    const { data } = await api.post(`/v2/content/publish`, {});
    return data || null;
  } catch (err) {
    throw contentError(
      err,
      'Could not publish just now. Your draft is safe. Please try again in a moment.'
    );
  }
}

export async function restoreDraftToLastPublish() {
  try {
    const { data } = await api.post(`/v2/content/restore`, {});
    return data || null;
  } catch (err) {
    throw contentError(
      err,
      'Could not restore to the published version. Please try again.'
    );
  }
}
